import { useEffect, useMemo, useState } from "react";

import { NoticeDetails } from "../../lib/hooks/api/notices/useNotice";
import { Flex } from "../../styles/utility-components";
import Collapsible from "../ui/collapsible/Collapsible";
import SummaryPoint from "./SummaryPoint";
import { ORDER_OF_SECTIONS_ON_PAGE, SummaryValue } from "./utils";

type Props = {
  aiSummary: NoticeDetails["aiSummary"];
  highlights?: string[];
};

function SummarySection({ aiSummary, highlights }: Props) {
  const [panelOpen, setPanelOpen] = useState(false);

  const summaryPoints = useMemo(
    () =>
      ORDER_OF_SECTIONS_ON_PAGE.map((section) => ({
        content: aiSummary?.[section] ?? null,
        attributeName: section,
      })).filter((section) => section.content !== null) as SummaryValue[],
    [aiSummary],
  );

  useEffect(() => {
    if (!panelOpen && summaryPoints.length > 0 && summaryPoints.some((sp) => sp.content)) {
      setPanelOpen(true);
    }
    // We don't want this hook to depend on panelOpen, because we don't want it to be re-run
    // every time the user toggles the panel open & closed; we only want the panel to automatically
    // open when the data loads
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summaryPoints]);

  if (!aiSummary || summaryPoints.length === 0) {
    return null;
  }

  return (
    <Collapsible
      open={panelOpen}
      onOpenChange={setPanelOpen}
      title="Your AI summary"
      subtitle="Get the key highlights from this notice"
      icon="sparklesFilled"
      isBordered
      borderColor="yellow600"
      iconProps={{ color: "yellow500", size: 28 }}
      headerTriggerProps={{
        backgroundColor: "yellow50",
        backgroundColorHover: "yellow100",
      }}
      contentWrapperStyles={{ paddingTop: 24 }}
    >
      <Flex column gap={24}>
        {summaryPoints.map((sp) => (
          <SummaryPoint summaryPoint={sp} key={sp.attributeName} wordsToHighlight={highlights} />
        ))}
      </Flex>
    </Collapsible>
  );
}

export default SummarySection;
